import { DateTime } from '@ksyos/date-time';
import { Choicebox, Radio, RadioGroup } from '@ksyos/design-system';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { stringToUuid, type Uuid } from '@common/utils/uuid';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { type FundingServicePart, getFundingServiceParts } from '@frontend/services/api/fundingServicePartsApiService';
import type { ValidationErrors } from '../../../utils/validation';
import type { IBreadcrumbItem } from '../../elements/common';
import { fundingSettingsAddSchema, fundingSettingsEditSchema } from '../../../../common/validation/fundingSettings';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { OverlapChecker } from '../../../utils/overlapChecker';
import { RateLimiter } from '../../../utils/rateLimiter';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import {
    CancelButton,
    DateInput,
    FormContainer,
    FormGroup,
    FormLabel,
    Header,
    InputCheckbox,
    InputContainer,
    InputCurrency,
    InputGroup,
    Loading,
    OverlapMessage,
    SaveSubmit,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';

type IFundingSettingsAddEditProps = IWithRouterProps;

interface IFundingSetting {
    compensationCharge: number | null;
    endDate: string | null;
    financedByPreviousService: boolean;
    fundingServicePartId: Uuid | null;
    isAlternativeFinancing: boolean;
    sendInvoice: boolean;
    serviceGroupSettingId: string | null;
    serviceOrganizationSettingId: string | null;
    startDate: string | null;
}

interface IFundingSettingsAddEditState {
    allFundingServiceParts: FundingServicePart[];
    errors: ValidationErrors;

    fundingSetting?: IFundingSetting;

    isSaving: boolean;
    isUploading?: boolean;
    minimumStartDate?: string;

    overlap?: {
        endDate: string | null;
        id: string;
        startDate: string;
    };
}

class InnerFundingSettingsAddEdit extends Component<IFundingSettingsAddEditProps, IFundingSettingsAddEditState> {
    private readonly atMostOncePerSecond = new RateLimiter(1000);
    private readonly overlapChecker = new OverlapChecker('/api/fundingSettings');

    private readonly abortController = new AbortController();

    constructor(props: IFundingSettingsAddEditProps, context?: {}) {
        super(props, context);

        this.state = {
            allFundingServiceParts: [],
            isSaving: false,
            errors: emptyValidationErrors,
        };
    }

    public async componentDidMount() {
        await this.setup(this.props.router.params.id);
    }

    public async componentDidUpdate(prevProps: IFundingSettingsAddEditProps) {
        if (this.props.router.params.id !== prevProps.router.params.id) {
            await this.setup(this.props.router.params.id);
        }
    }

    public componentWillUnmount() {
        this.abortController.abort();
    }

    public render() {
        const breadcrumbs: IBreadcrumbItem[] = [
            {
                text:
                    (this.isEdit() ? translateKey('common.edit') : translateKey('common.new')) +
                    ' ' +
                    translateKey('fundingSettings.fundingSetting'),
            },
        ];

        return (
            <FixedWidthTemplate breadcrumb={breadcrumbs}>
                <Header
                    content={
                        (this.isEdit() ? translateKey('common.edit') : translateKey('common.new')) +
                        ' ' +
                        translateKey('fundingSettings.fundingSetting')
                    }
                />

                <Loading
                    item={this.state.fundingSetting}
                    loaded={(fundingSetting) => (
                        <FormContainer onSubmit={async () => await this.save()}>
                            {this.state.overlap && (
                                <OverlapMessage
                                    endDate={this.state.overlap.endDate}
                                    link={this.getUrlWithParams(`/fundingSettings/${this.state.overlap.id}`)}
                                    message={translateKey('common.overlapMessage')}
                                    startDate={this.state.overlap.startDate}
                                />
                            )}
                            <FormGroup hasErrors={this.state.errors.has('startDate')}>
                                <FormLabel isRequired>{translateKey('common.startDate')}</FormLabel>
                                <InputGroup>
                                    <DateInput
                                        error={this.state.errors.get('startDate')}
                                        minDate={this.state.minimumStartDate}
                                        onChange={async (value) => await this.handleChange('startDate', value)}
                                        onDoValidate={() => this.validateOne('startDate')}
                                        value={fundingSetting.startDate}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <FormGroup hasErrors={this.state.errors.has('endDate')}>
                                <FormLabel>{translateKey('common.endDate')}</FormLabel>
                                <InputGroup>
                                    <DateInput
                                        error={this.state.errors.get('endDate')}
                                        onChange={async (value) => await this.handleChange('endDate', value)}
                                        onDoValidate={() => this.validateOne('endDate')}
                                        value={fundingSetting.endDate}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>{translateKey('serviceProvider.sendInvoice')}</FormLabel>
                                <InputGroup>
                                    <InputCheckbox
                                        checked={fundingSetting.sendInvoice}
                                        error={this.state.errors.get('sendInvoice')}
                                        onChange={async (value) => {
                                            await this.handleChange('sendInvoice', value);
                                            if (value && fundingSetting.isAlternativeFinancing) {
                                                await this.handleChange('isAlternativeFinancing', false);
                                            }
                                            if (!value) {
                                                await this.handleChange('fundingServicePartId', null);
                                            }
                                        }}
                                        onDoValidate={async () => this.validateOne('sendInvoice')}
                                    />
                                </InputGroup>
                            </FormGroup>

                            {fundingSetting.sendInvoice && this.state.allFundingServiceParts.length > 0 && (
                                <FormGroup>
                                    <FormLabel>{translateKey('finance.fundingServicePart.shortName')}</FormLabel>
                                    <InputGroup>
                                        <RadioGroup
                                            layout="column"
                                            name="useFundingParts"
                                            onChange={(value) => {
                                                if (value.target.value === 'all') {
                                                    this.handleChange('fundingServicePartId', null);
                                                } else {
                                                    this.handleChange(
                                                        'fundingServicePartId',
                                                        this.state.allFundingServiceParts[0].id
                                                    );
                                                }
                                            }}
                                            value={fundingSetting.fundingServicePartId === null ? 'all' : 'part'}
                                        >
                                            <Radio
                                                label={translateKey('finance.fundingServicePart.all')}
                                                value="all"
                                            />
                                            <Radio
                                                label={translateKey('finance.fundingServicePart.specific')}
                                                value="part"
                                            />
                                        </RadioGroup>
                                    </InputGroup>
                                </FormGroup>
                            )}

                            {fundingSetting.fundingServicePartId !== null && (
                                <FormGroup>
                                    <FormLabel>{translateKey('finance.fundingServicePart.longName')}</FormLabel>
                                    <RadioGroup
                                        layout="row"
                                        name="useFundingParts"
                                        onChange={(value) => {
                                            this.handleChange('fundingServicePartId', value.target.value as Uuid);
                                        }}
                                        value={fundingSetting.fundingServicePartId}
                                    >
                                        {this.state.allFundingServiceParts.map((part) => (
                                            <Choicebox
                                                key={part.id}
                                                label={part.name}
                                                value={part.id}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormGroup>
                            )}

                            <FormGroup>
                                <FormLabel>{translateKey('serviceProvider.compensation')}</FormLabel>
                                <InputContainer small>
                                    <InputCurrency
                                        error={this.state.errors.get('compensationCharge')}
                                        onChange={async (value) => await this.handleChange('compensationCharge', value)}
                                        onDoValidate={() => this.validateOne('compensationCharge')}
                                        value={fundingSetting.compensationCharge}
                                    />
                                </InputContainer>
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>{translateKey('groupSettings.isAlternativeFinancing')}</FormLabel>
                                <InputGroup>
                                    <InputCheckbox
                                        checked={fundingSetting.isAlternativeFinancing}
                                        error={this.state.errors.get('isAlternativeFinancing')}
                                        onChange={async (value) => {
                                            await this.handleChange('isAlternativeFinancing', value);
                                            if (value && fundingSetting.sendInvoice) {
                                                await this.handleChange('sendInvoice', false);
                                                await this.handleChange('fundingServicePartId', null);
                                            }
                                        }}
                                        onDoValidate={async () => this.validateOne('isAlternativeFinancing')}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <FormGroup>
                                <FormLabel tooltipText={translateKey('groupSettings.alreadyFinancedTooltip')}>
                                    {translateKey('groupSettings.alreadyFinanced')}
                                </FormLabel>
                                <InputGroup>
                                    <InputCheckbox
                                        checked={fundingSetting.financedByPreviousService}
                                        error={this.state.errors.get('financedByPreviousService')}
                                        onChange={async (value) =>
                                            await this.handleChange('financedByPreviousService', value)
                                        }
                                        onDoValidate={async () => this.validateOne('financedByPreviousService')}
                                    />
                                </InputGroup>
                            </FormGroup>

                            <CancelButton
                                disabled={this.state.isSaving}
                                to={this.getReturnUrl()}
                            />
                            <SaveSubmit
                                disabled={this.state.isSaving || !!this.state.overlap || this.state.isUploading}
                            />
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async setup(fundingSettingId: string | undefined) {
        if (fundingSettingId) {
            await this.loadFundingSetting(fundingSettingId);
        } else {
            await this.setState({
                fundingSetting: {
                    compensationCharge: null,
                    endDate: null,
                    financedByPreviousService: false,
                    fundingServicePartId: null,
                    isAlternativeFinancing: false,
                    sendInvoice: false,
                    serviceGroupSettingId: this.props.router.searchParams.get('serviceGroupSettingId'),
                    serviceOrganizationSettingId: this.props.router.searchParams.get('serviceOrganizationSettingId'),
                    startDate: DateTime.now().format('YYYY-MM-DD'),
                },
            });
        }

        await this.loadFundingServiceParts();
        await this.setState({ overlap: undefined });
        await this.checkOverlap();

        await this.getMinimumStartDate();
    }

    private async loadFundingServiceParts() {
        const serviceId = stringToUuid(this.props.router.searchParams.get('serviceId') ?? '');
        const parts = await getFundingServiceParts(serviceId, this.abortController.signal);
        parts.sort((a, b) => a.name.localeCompare(b.name));
        await this.setState({ allFundingServiceParts: parts });
    }

    private async getMinimumStartDate() {
        const serviceGroupSettingId = this.props.router.searchParams.get('serviceGroupSettingId');
        const serviceOrganizationSettingId = this.props.router.searchParams.get('serviceOrganizationSettingId');
        let url;
        if (serviceGroupSettingId) {
            url = `/api/serviceGroupSettings/${serviceGroupSettingId}`;
        } else if (serviceOrganizationSettingId) {
            url = `/api/serviceOrganizationSettings/${serviceOrganizationSettingId}`;
        }

        if (url) {
            const result = await fetchJson(url, 'GET');
            await this.setState({ minimumStartDate: result.json.startDate });
        }
    }

    private isEdit() {
        return !!this.getParamFundingSettingId();
    }

    private getParamFundingSettingId() {
        return this.props.router.params.id;
    }

    private async handleChange<K extends keyof IFundingSetting>(name: K, value: IFundingSetting[K]) {
        await this.setState(({ fundingSetting }: { fundingSetting: IFundingSetting }) => ({
            fundingSetting: { ...fundingSetting, [name]: value },
        }));

        if (name === 'startDate' || name === 'endDate' || name == 'fundingServicePartId') {
            this.checkOverlap();
        }
    }

    private async validateOne(name: keyof IFundingSetting) {
        await this.setState(({ fundingSetting, errors }) => ({
            errors: updateValidationErrorsForOne(this.getSchema(), fundingSetting, errors, name),
        }));
    }

    private async checkOverlap() {
        const serviceGroupSettingId = this.props.router.searchParams.get('serviceGroupSettingId');
        const serviceOrganizationSettingId = this.props.router.searchParams.get('serviceOrganizationSettingId');
        this.atMostOncePerSecond.callMeMaybe(async () => {
            if (this.state.fundingSetting) {
                const checkParams: {
                    excludeWhenFundingServicePart: 'true' | 'false';
                    serviceGroupSettingId?: string;
                    serviceOrganizationSettingId?: string;
                } = {
                    excludeWhenFundingServicePart:
                        this.state.fundingSetting.fundingServicePartId === null ? 'false' : 'true',
                };

                if (serviceGroupSettingId) {
                    checkParams.serviceGroupSettingId = serviceGroupSettingId;
                } else if (serviceOrganizationSettingId) {
                    checkParams.serviceOrganizationSettingId = serviceOrganizationSettingId;
                }

                const overlap = await this.overlapChecker.check(
                    checkParams,
                    this.state.fundingSetting.startDate,
                    this.state.fundingSetting.endDate,
                    this.getParamFundingSettingId()
                );
                await this.setState({ overlap });
            }
        });
    }

    private async loadFundingSetting(fundingSettingId: string) {
        const url = `/api/fundingSettings/${fundingSettingId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ fundingSetting: result.json });
    }

    private async save() {
        if (this.state.overlap) {
            return;
        }

        const { errors, item } = validate(this.getSchema(), this.state.fundingSetting);
        await this.setState({ errors });
        if (errors.size > 0) {
            return false;
        }

        let url = '/api/fundingSettings';
        if (this.isEdit()) {
            url += `/${this.getParamFundingSettingId()}`;
        }
        await this.setState({ isSaving: true });

        await fetchJson(url, 'POST', item);

        this.props.router.navigate(this.getReturnUrl());
    }

    private getReturnUrl() {
        const serviceGroupSettingId = this.props.router.searchParams.get('serviceGroupSettingId');
        const serviceOrganizationSettingId = this.props.router.searchParams.get('serviceOrganizationSettingId');
        let url = '';
        if (serviceGroupSettingId) {
            url = `/serviceGroupSettings/${serviceGroupSettingId}`;
        } else if (serviceOrganizationSettingId) {
            url = `/serviceOrganizationSettings/${serviceOrganizationSettingId}`;
        }

        return this.getUrlWithParams(url, true);
    }

    private getUrlWithParams(baseUrl: string, isReturnUrl?: boolean) {
        const serviceGroupSettingId = this.props.router.searchParams.get('serviceGroupSettingId');
        const serviceOrganizationSettingId = this.props.router.searchParams.get('serviceOrganizationSettingId');
        const serviceId = this.props.router.searchParams.get('serviceId');
        const groupId = this.props.router.searchParams.get('groupId');
        const organizationId = this.props.router.searchParams.get('organizationId');

        const includeServiceId = serviceId && !(isReturnUrl && (groupId || organizationId));

        const params = new URLSearchParams({
            ...(!isReturnUrl && serviceGroupSettingId && { serviceGroupSettingId }),
            ...(!isReturnUrl && serviceOrganizationSettingId && { serviceOrganizationSettingId }),
            ...(includeServiceId && { serviceId }),
            ...(groupId && { groupId }),
            ...(organizationId && { organizationId }),
        });

        return `${baseUrl}?${params.toString()}`;
    }

    private getSchema() {
        return this.isEdit() ? fundingSettingsEditSchema : fundingSettingsAddSchema;
    }
}

export const FundingSettingsAddEdit = withRouter(InnerFundingSettingsAddEdit);
